import React, { useState } from 'react'
import { Role, User, UserFormValues } from '../../store/types'
import { Controller, Control, UseFormSetValue, FieldErrors, UseFormWatch } from 'react-hook-form'
import { Stack, FormControlLabel, Checkbox, Typography, Tooltip } from '@mui/material'
import { ExtraSmallSquareButton } from '../../styles/ButtonStyles'
import { HelpOutline, RestartAlt } from '@mui/icons-material'
import { t } from 'i18next'
import { useGetRolesQuery } from '../../store/redux/reducers/rolesSlice'
import defaultProps from '../../config/defaultProps'
import { ErrorMessage } from '@hookform/error-message'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import RoleType from '../../store/types/enums/RoleType'

type ProfileViewRolesSectionProps = {
  control: Control<UserFormValues, any>
  setValue: UseFormSetValue<UserFormValues>
  user: User | null
  errors: FieldErrors<UserFormValues>
  watch: UseFormWatch<UserFormValues>
}

const ProfileViewRolesSection = ({ control, setValue, user, errors, watch }: ProfileViewRolesSectionProps) => {
  const { data: roles } = useGetRolesQuery(defaultProps)
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(user?.roles || [])
  const { data: managers } = useGetLightUsersQuery({ page: 0, size: 2, 'roles.name': RoleType.Management })

  const watchedRoles = watch('roles')

  React.useMemo(() => {
    if (watchedRoles !== undefined) {
      setSelectedRoles(watchedRoles)
      return
    }

    if (user?.roles !== undefined) {
      setSelectedRoles(user?.roles)
      return
    }

    setSelectedRoles([])
  }, [user?.roles, watchedRoles])

  const handleRoleCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>, role: Role) => {
    let rolesWithoutBaseUser = selectedRoles.filter(r => r.name !== RoleType.BaseUser)
    let updatedRoles: Role[]

    if (e.target.checked) {
      updatedRoles = [...rolesWithoutBaseUser, role]
      setSelectedRoles(updatedRoles)
      setValue('roles', updatedRoles, { shouldDirty: true, shouldValidate: true })
    } else {
      updatedRoles = selectedRoles.filter(r => r.id !== role.id)
      setSelectedRoles(updatedRoles)
      setValue('roles', updatedRoles, { shouldDirty: true, shouldValidate: true })
    }
  }

  const handleResetRoles = () => {
    setSelectedRoles(user?.roles || [])
  }

  return (
    <Controller
      name="roles"
      control={control}
      render={({ fieldState }) => (
        <Stack>
          <Stack justifyContent="space-between" direction="row" width="100%">
            <Stack direction="row" gap={1} alignItems="center">
              <Typography component={'h4'} variant="h5" color={fieldState.error ? 'error.main' : 'text.highEmphasis'}>
                {t('page.usersProfile.form.headers.roles')}
              </Typography>
              <Tooltip
                title={<Typography variant="caption">{t('page.usersProfile.form.headers.rolesInfo')}</Typography>}
              >
                <HelpOutline sx={{ color: 'text.mediumEmphasis', fontSize: 18 }} />
              </Tooltip>
            </Stack>
            <Tooltip title={t('page.usersProfile.form.headers.resetRoles')}>
              <ExtraSmallSquareButton color="secondary" onClick={handleResetRoles}>
                <RestartAlt />
              </ExtraSmallSquareButton>
            </Tooltip>
          </Stack>
          <Stack sx={{ pt: 1 }}>
            <ErrorMessage
              errors={errors}
              name="roles"
              render={({ message }) => <Typography sx={{ color: 'error.main' }}>{message}</Typography>}
            />
          </Stack>
          {roles?.map(r => {
            if (r.name === RoleType.BaseUser) return null
            return (
              <FormControlLabel
                sx={{
                  display: 'flex',
                  flex: 'row',
                  alignItems: 'flex-start',
                  alignSelf: 'start',
                  minWidth: 0,
                  width: '100%',
                  overflow: 'hidden',
                  '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden' },
                }}
                control={
                  <Checkbox
                    checked={selectedRoles.map(r => r.id).includes(r.id)}
                    onChange={e => handleRoleCheckboxClick(e, r)}
                    sx={{
                      svg: {
                        color: fieldState.error ? 'error.main' : '',
                      },
                    }}
                  />
                }
                label={
                  <Stack direction="column" pt={1.25} color={fieldState.error ? 'error.main' : 'text.base'}>
                    <Tooltip title={t(`roles.${r.name}`)} placement="top">
                      <Typography sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        {t(`roles.${r.name}`)}
                      </Typography>
                    </Tooltip>
                    {r.name === RoleType.BaseUser && (
                      <Typography
                        variant="helper"
                        fontStyle="italic"
                        color={fieldState.error ? 'error.main' : 'text.disabled'}
                        sx={{ width: 'auto', alignSelf: 'start' }}
                      >
                        {t('page.usersProfile.alert.roles')}
                      </Typography>
                    )}
                  </Stack>
                }
                key={`role-checkbox-${r.id}`}
              />
            )
          })}
        </Stack>
      )}
      rules={{
        validate: {
          areThereManagers: _ => {
            if (!user?.roles.map(r => r.name).includes(RoleType.Management)) return true
            if (selectedRoles.map(r => r.name).includes(RoleType.Management)) return true
            if (managers?.totalElements === 1) return t('page.usersProfile.form.errors.managerRequired')
            return true
          },
          recruiterRole: value => {
            const roleNames = new Set(value?.map((r: Role) => r.name))

            const isRecruiterOnly =
              roleNames.has(RoleType.Recruiter) &&
              ![RoleType.Employee, RoleType.Leader, RoleType.ProjectLeader, RoleType.Management].some(role =>
                roleNames.has(role)
              )

            return isRecruiterOnly ? t('page.usersProfile.form.errors.onlyRecruiter') : true
          },
        },
      }}
    />
  )
}

export default ProfileViewRolesSection
